import React from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";

import variableColors from "../_App.module.scss";

const { darkColor, appBackgroundColorWhiteSmoke, secondaryColor, primaryColor, customBgColor } = variableColors;

const Container = styled.div`
  height: calc(100vh - var(--constantHeights));
  border: 1px solid ${darkColor};
  max-width: 60rem;
  margin: 1rem auto;
  padding: 1.5rem;
  font-family: inherit;
  color: ${darkColor};
  line-height: 1.6;
  background-color: #ffffff; //* White
  border-radius: 0.5rem;
  box-shadow: 0 0.25rem 0.75rem ${darkColor};
  overflow-y: auto;
`;

const Header = styled.header`
  border-bottom: 2px solid ${appBackgroundColorWhiteSmoke};
  padding-bottom: 1.5rem;
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  font-size: 1.75rem;
  color: ${darkColor};
  margin: 0 0 0.5rem 0;
`;

const Subtitle = styled.p`
  font-size: 0.875rem;
  color: ${secondaryColor};
  margin: 0;
`;

const Section = styled.section`
  margin-bottom: 1.5rem;
`;

const SectionTitle = styled.h2`
  font-size: 1.2rem;
  color: ${darkColor};
  margin-top: 1.5rem;
  margin-bottom: 1rem;
  border-left: 0.25rem solid ${primaryColor};
  padding-left: 0.5rem;
`;

const Paragraph = styled.p`
  font-size: 1rem;
  margin-bottom: 1rem;
  text-align: justify;
`;

const List = styled.ul`
  padding-left: 1.5rem;
  margin-bottom: 1rem;
`;

const OrderedList = styled.ol`
  padding-left: 1.5rem;
  margin-bottom: 1rem;
`;

const ListItem = styled.li`
  font-size: 1rem;
  margin-bottom: 0.5rem;
`;

const Highlight = styled.span`
  background-color: ${customBgColor};
  padding: 2px 0.5rem;
  border-radius: 0.25rem;
  font-weight: 500;
`;

const Strong = styled.strong`
  color: ${darkColor};
  font-weight: 600;
`;

const PrivacyPolicy = (): JSX.Element => {
  const { t } = useTranslation();

  const lastUpdateDate: string = "2026.06.19";

  return (
    <React.Fragment>
      <Container>
        <Header>
          <Title>{t("Polityka Prywatności i Obowiązek Informacyjny (RODO & CCPA/CPRA)")}</Title>
          <Subtitle>
            {t("Ostatnia aktualizacja")} <b>{lastUpdateDate}</b>
          </Subtitle>
        </Header>

        <Section>
          <SectionTitle>{t("1. Administrator Danych Osobowych")}</SectionTitle>
          <Paragraph>
            {t("Administratorem danych osobowych użytkowników Aplikacji jest")}{" "}
            <Highlight>{"goEuropa, 28 Czerwca 1956r. nr 406, 61-441 Poznań, Polska/Poland"}</Highlight>
            {", email: "}
            <Highlight>{"info@goeuropa.eu"}</Highlight>
            {"."}
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>{t("2. Jakie dane zbieramy i w jakim celu?")}</SectionTitle>
          <Paragraph>{t("Ograniczamy zbieranie danych do niezbędnego minimum. Przetwarzamy wyłącznie")}</Paragraph>
          <List>
            <ListItem>
              <Strong>{t("Służbowy adres e-mail")}</Strong>{" "}
              {t("Pobierany podczas logowania za pośrednictwem dostawcy tożsamości")}{" "}
              <Strong>{"Auth0 (Okta, Inc.)/Keycloak"}</Strong>
              {". "}
              {t(
                "Dane te są przetwarzane wyłącznie w celu uwierzytelnienia użytkownika, przyznania dostępu do konta oraz zapewnienia bezpieczeństwa Aplikacji."
              )}
            </ListItem>
          </List>
        </Section>

        <Section>
          <SectionTitle>{t("3. Komu przekazujemy dane? (Odbiorcy danych)")}</SectionTitle>
          <Paragraph>
            {t(
              "W przypadku TC2.0, w celu zapewnienia bezpiecznego logowania, Twoje dane (adres e-mail) są powierzane firmie Auth0 (Okta, Inc.), która działa jako procesor danych (podmiot przetwarzający) na podstawie stosownej umowy (Data Processing Addendum). W przypadku TC3.0 dane są przechowywane przez nas na serwerze Keycloak. W obu przypadkach dane są przechowywane na serwerach zlokalizowanych na terenie Unii Europejskiej. Wersję TC możesz sprawdzić w ustawieniach."
            )}
          </Paragraph>

          <Paragraph>
            <Strong>{t("W rozumieniu przepisów CCPA/CPRA")}</Strong>{" "}
            {t(
              "Administrator nie sprzedaje ani nie udostępnia adresów e-mail ani żadnych innych danych użytkowników podmiotom trzecim w celach komercyjnych ani reklamowych."
            )}
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>{t("4. Pliki Cookies i Web Storage (localStorage)")}</SectionTitle>
          <Paragraph>
            {t(
              "Aplikacja nie wykorzystuje żadnych plików cookies ani technologii śledzących o charakterze analitycznym, statystycznym lub marketingowym."
            )}
          </Paragraph>
          <Paragraph>{t("Wykorzystujemy wyłącznie technologie ściśle niezbędne do działania Aplikacji, w tym")}</Paragraph>
          <List>
            <ListItem>
              <Strong>{t("Tokeny sesyjne Auth0/Keycloak (localStorage/sessionStorage)")}</Strong>{" "}
              {t(
                "Przechowywane tymczasowo w celu utrzymania sesji użytkownika po zalogowaniu, aby użytkownik nie musiał logować się ponownie przy każdym odświeżeniu strony."
              )}
            </ListItem>
            <ListItem>
              <Strong>{t("Stan aplikacji")}</Strong>{" "}
              {t("Identyfikatory niezbędne do poprawnego renderowania interfejsu użytkownika.")}
            </ListItem>
          </List>
          <Paragraph>
            {t(
              "Zgodnie z prawem, technologie ściśle niezbędne nie wymagają uprzedniej zgody użytkownika, gdyż są warunkiem technicznym świadczenia usługi. Możesz zarządzać nimi z poziomu ustawień swojej przeglądarki internetowej."
            )}
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle>{t("5. Jak długo przechowujemy dane?")}</SectionTitle>
          <List>
            <ListItem>{t("Dane sesyjne w cookies/localStorage są usuwane po wylogowaniu lub wygaśnięciu sesji.")}</ListItem>
            <ListItem>
              {t(
                "Służbowy adres e-mail jest przechowywany w systemie Auth0/Keycloak przez okres aktywności Twojego konta w Aplikacji lub do momentu zgłoszenia żądania jego usunięcia."
              )}
            </ListItem>
          </List>
        </Section>

        <Section>
          <SectionTitle>{t("6. Twoje Prawa (RODO & CCPA/CPRA)")}</SectionTitle>
          <Paragraph>
            {t("W zależności od Twojej lokalizacji (UE / Kalifornia), przysługują Ci następujące prawa")}
          </Paragraph>
          <OrderedList>
            <ListItem>{t("Prawo dostępu do danych oraz otrzymania ich kopii.")}</ListItem>
            <ListItem>{t("Prawo do sprostowania (poprawiania) swoich danych.")}</ListItem>
            <ListItem>{t("Prawo do usunięcia danych ('prawo do bycia zapomnianym').")}</ListItem>
            <ListItem>{t("Prawo do ograniczenia przetwarzania.")}</ListItem>
            <ListItem>{t("Prawo do sprzeciwu wobec przetwarzania danych.")}</ListItem>
            <ListItem>
              {t("Prawo do nie bycia dyskryminowanym za korzystanie ze swoich praw prywatności (wymóg CCPA).")}
            </ListItem>
          </OrderedList>
          <Paragraph>
            {t(
              "Aby skorzystać ze swoich praw (w tym zgłosić chęć usunięcia konta i adresu e-mail z systemów Aplikacji oraz Auth0/Keycloak), skontaktuj się z nami pod adresem"
            )}{" "}
            <Strong>
              <Highlight>{"info@goeuropa.eu"}</Highlight>
            </Strong>
            {". "}
            {t("Zgłoszenia realizujemy bez zbędnej zwłoki, nie później niż w terminie 30 dni.")}
          </Paragraph>
        </Section>
      </Container>
    </React.Fragment>
  );
};

export default PrivacyPolicy;
